<script lang="ts">
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import Navbar from '../../components/Navbar.svelte';
	import AdminSidebar from '../../components/dashboard/AdminSidebar.svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';
	import type { RepositorySearchResult } from '../../api_contracts/app';

	let isAdmin = false;
	let loading = $state(false);
	let repositories: RepositorySearchResult[] = $state([]);
	let currentPage = $state(1);
	let totalPages = $state(1);
	let totalCount = $state(0);
	let limit = 20;
	let error = $state('');

	onMount(async () => {
		const profile = get(appStore).profile;
		if (!profile || profile.userRole !== 'admin') {
			push('/');
			return;
		}
		isAdmin = true;
		await fetchRepositories();
	});

	async function fetchRepositories() {
		try {
			loading = true;
			error = '';
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getAllRepositories({
				token,
				page: currentPage,
				limit
			});

			if (response.response.successFull) {
				repositories = response.response.repositories;
				totalCount = response.response.totalCount;
				totalPages = response.response.totalPages;
			} else {
				error = response.response.reason || 'Failed to load repositories';
			}
		} catch (e) {
			console.error('Error fetching repositories:', e);
			error = 'Failed to load repositories';
		} finally {
			loading = false;
		}
	}

	function goToPage(page: number) {
		currentPage = page;
		fetchRepositories();
	}

	function formatDate(dateString: string): string {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}
</script>

<Navbar />

<div class="page-wrapper">
	{#if isAdmin}
		<div class="page-layout">
			<AdminSidebar />
			<div class="page-main">
				<div class="page-header">
					<h1>Repositories Management</h1>
					<p>View and manage all repositories</p>
				</div>

				<div class="content-card">
					<div class="card-header">
						<h2>All Repositories</h2>
						<span class="count">{totalCount} repositories</span>
					</div>

					{#if loading}
						<div class="loading">Loading repositories...</div>
					{:else if error}
						<div class="error">{error}</div>
					{:else if repositories.length === 0}
						<div class="empty-state">
							<p>No repositories found</p>
						</div>
					{:else}
						<div class="table-container">
							<table class="data-table">
								<thead>
									<tr>
										<th>Repository</th>
										<th>Owner</th>
										<th>Visibility</th>
										<th>Stars</th>
										<th>Forks</th>
										<th>Created</th>
									</tr>
								</thead>
								<tbody>
									{#each repositories as repo}
										<tr>
											<td>
												<a href="#/users/{repo.ownerName}/repositories/{repo.name}" class="repo-link">
													{repo.name}
												</a>
												{#if repo.description}
													<div class="repo-desc">{repo.description}</div>
												{/if}
											</td>
											<td>{repo.ownerName}</td>
											<td>
												<span class="visibility-badge {repo.visibility}">
													{repo.visibility}
												</span>
											</td>
											<td>{repo.starCount}</td>
											<td>{repo.forkCount}</td>
											<td>{formatDate(repo.createdAt)}</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>

						{#if totalPages > 1}
							<div class="pagination">
								<button
									class="page-btn"
									disabled={currentPage === 1}
									onclick={() => goToPage(currentPage - 1)}
								>
									Previous
								</button>
								<span class="page-info">
									Page {currentPage} of {totalPages}
								</span>
								<button
									class="page-btn"
									disabled={currentPage === totalPages}
									onclick={() => goToPage(currentPage + 1)}
								>
									Next
								</button>
							</div>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.page-wrapper {
		min-height: 100vh;
		background: var(--bg-primary, #0d1117);
	}

	.page-layout {
		display: flex;
		min-height: calc(100vh - 60px);
	}

	.page-main {
		flex: 1;
		padding: 2rem;
		overflow-y: auto;
	}

	.page-header {
		margin-bottom: 2rem;
	}

	.page-header h1 {
		font-size: 2rem;
		font-weight: 600;
		margin: 0 0 0.5rem 0;
		color: var(--text-primary, #e6edf3);
	}

	.page-header p {
		color: var(--text-secondary, #8b949e);
		margin: 0;
	}

	.content-card {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.card-header h2 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0;
		color: var(--text-primary, #e6edf3);
	}

	.count {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.table-container {
		overflow-x: auto;
	}

	.data-table {
		width: 100%;
		border-collapse: collapse;
	}

	.data-table thead th {
		text-align: left;
		padding: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text-secondary, #8b949e);
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.empty-state {
		padding: 3rem 1rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
	}

	.empty-state p {
		margin: 0.5rem 0;
	}

	.hint {
		font-size: 0.875rem;
		color: var(--text-tertiary, #6e7681);
	}

	.loading,
	.error {
		padding: 2rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
	}

	.error {
		color: #f85149;
	}

	.data-table tbody td {
		padding: 0.75rem;
		border-bottom: 1px solid var(--border-secondary, #21262d);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
	}

	.data-table tbody tr:hover {
		background: var(--bg-tertiary, #1c2128);
	}

	.repo-link {
		color: var(--accent-primary, #58a6ff);
		text-decoration: none;
		font-weight: 500;
	}

	.repo-link:hover {
		text-decoration: underline;
	}

	.repo-desc {
		font-size: 0.8125rem;
		color: var(--text-secondary, #8b949e);
		margin-top: 0.25rem;
	}

	.visibility-badge {
		padding: 0.25rem 0.75rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
		text-transform: capitalize;
	}

	.visibility-badge.public {
		background: rgba(126, 184, 69, 0.1);
		color: #7eb845;
	}

	.visibility-badge.private {
		background: rgba(248, 81, 73, 0.1);
		color: #f85149;
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 1rem;
		margin-top: 1.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--border-primary, #30363d);
	}

	.page-btn {
		padding: 0.5rem 1rem;
		border-radius: 6px;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.page-btn:hover:not(:disabled) {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
	}

	.page-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.page-info {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}
</style>
